import type {
  QueryClient,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import type {
  EventIdPublic,
  EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";
import {
  AdminParticipantAssumptionsSchema,
  type AdminParticipantAssumptions,
} from "@shared/schemas/admin-participant-assumptions.schemas";
import {
  AdminParticipantEnrichmentSchema,
  type AdminParticipantEnrichment,
} from "@shared/schemas/admin-participant-enrichment.schemas";
import {
  AdminParticipantReasonsToMeetSchema,
  type AdminParticipantReasonsToMeet,
} from "@shared/schemas/admin-participant-reasons-to-meet.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { createParticipantInspectorAdminApi } from "./participant-inspector-admin.api";

export interface RecomputeReasonsToMeetVariables {
  /** Recompute even when the inputs have not changed since the last run. */
  readonly force: boolean;
}

/**
 * The admin participant inspector's reads. One tab is one route, so a tab
 * only ever fetches its own section — nothing here is bundled into a single
 * aggregate. Exported so a feature that mutates a participant can invalidate
 * everything the inspector shows for it.
 */
export const PARTICIPANT_INSPECTOR_ADMIN_QUERY_KEYS = {
  participant: (
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ) =>
    [
      "participant-inspector-admin",
      "event",
      eventIdPublic,
      "participant",
      participantIdPublic,
    ] as const,
  assumptions: (
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ) =>
    [
      ...PARTICIPANT_INSPECTOR_ADMIN_QUERY_KEYS.participant(
        eventIdPublic,
        participantIdPublic,
      ),
      "assumptions",
    ] as const,
  enrichment: (
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ) =>
    [
      ...PARTICIPANT_INSPECTOR_ADMIN_QUERY_KEYS.participant(
        eventIdPublic,
        participantIdPublic,
      ),
      "enrichment",
    ] as const,
  reasonsToMeet: (
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ) =>
    [
      ...PARTICIPANT_INSPECTOR_ADMIN_QUERY_KEYS.participant(
        eventIdPublic,
        participantIdPublic,
      ),
      "reasons-to-meet",
    ] as const,
} as const;

// Assumptions change on participant activity (enrichment generation), never on
// a timer, so a short stale window is enough to keep tab switching instant
// without serving yesterday's state.
const INSPECTOR_STALE_TIME_MS = 30_000;
// While a run waits or computes, so the tab shows it land without a manual refresh.
const REASONS_TO_MEET_POLL_MS = 5_000;

export class ParticipantInspectorAdminHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createParticipantInspectorAdminApi(apiClient);
  }

  createHooks() {
    const { api } = this;
    return {
      useGetParticipantAssumptions: (
        eventIdPublic: EventIdPublic,
        participantIdPublic: EventParticipantIdPublic,
      ): UseQueryResult<AdminParticipantAssumptions> =>
        this.query({
          key: PARTICIPANT_INSPECTOR_ADMIN_QUERY_KEYS.assumptions(
            eventIdPublic,
            participantIdPublic,
          ),
          fn: () => api.getAssumptions(eventIdPublic, participantIdPublic),
          parse: (dto) => AdminParticipantAssumptionsSchema.parse(dto),
          staleTime: INSPECTOR_STALE_TIME_MS,
        }),

      useGetParticipantEnrichment: (
        eventIdPublic: EventIdPublic,
        participantIdPublic: EventParticipantIdPublic,
      ): UseQueryResult<AdminParticipantEnrichment> =>
        this.query({
          key: PARTICIPANT_INSPECTOR_ADMIN_QUERY_KEYS.enrichment(
            eventIdPublic,
            participantIdPublic,
          ),
          fn: () => api.getEnrichment(eventIdPublic, participantIdPublic),
          parse: (dto) => AdminParticipantEnrichmentSchema.parse(dto),
          staleTime: INSPECTOR_STALE_TIME_MS,
        }),

      useGetParticipantReasonsToMeet: (
        eventIdPublic: EventIdPublic,
        participantIdPublic: EventParticipantIdPublic,
      ): UseQueryResult<AdminParticipantReasonsToMeet> =>
        this.query({
          key: PARTICIPANT_INSPECTOR_ADMIN_QUERY_KEYS.reasonsToMeet(
            eventIdPublic,
            participantIdPublic,
          ),
          fn: () => api.getReasonsToMeet(eventIdPublic, participantIdPublic),
          parse: (dto) => AdminParticipantReasonsToMeetSchema.parse(dto),
          staleTime: INSPECTOR_STALE_TIME_MS,
          refetchInterval: (data) =>
            data !== undefined &&
            (data.pendingSince !== null || data.runningSince !== null)
              ? REASONS_TO_MEET_POLL_MS
              : false,
        }),

      useRecomputeParticipantReasonsToMeet: (
        eventIdPublic: EventIdPublic,
        participantIdPublic: EventParticipantIdPublic,
      ): UseMutationResult<void, Error, RecomputeReasonsToMeetVariables> =>
        this.voidMutation({
          fn: ({ force }: RecomputeReasonsToMeetVariables) =>
            api.recomputeReasonsToMeet(
              eventIdPublic,
              [participantIdPublic],
              force,
            ),
          onSuccess: (): void => {
            this.invalidateQueries(
              PARTICIPANT_INSPECTOR_ADMIN_QUERY_KEYS.reasonsToMeet(
                eventIdPublic,
                participantIdPublic,
              ),
            );
          },
        }),

      useRecomputeEventReasonsToMeet: (
        eventIdPublic: EventIdPublic,
      ): UseMutationResult<void, Error, RecomputeReasonsToMeetVariables> =>
        this.voidMutation({
          fn: ({ force }: RecomputeReasonsToMeetVariables) =>
            api.recomputeReasonsToMeet(eventIdPublic, null, force),
        }),

      useRegenerateDirectoryEmbeddings: (
        eventIdPublic: EventIdPublic,
      ): UseMutationResult<void, Error, void> =>
        this.voidMutation({
          fn: () => api.regenerateDirectoryEmbeddings(eventIdPublic),
        }),

      useRegenerateParticipantAssumptions: (
        eventIdPublic: EventIdPublic,
        participantIdPublic: EventParticipantIdPublic,
      ): UseMutationResult<void, Error, void> =>
        this.voidMutation({
          fn: () =>
            api.regenerateAssumptions(eventIdPublic, participantIdPublic),
          onSuccess: (): void => {
            // Only queues the generation, so this refetches the block as it
            // stands; the regenerated one lands seconds later, on a later read.
            this.invalidateQueries(
              PARTICIPANT_INSPECTOR_ADMIN_QUERY_KEYS.assumptions(
                eventIdPublic,
                participantIdPublic,
              ),
            );
          },
        }),
    };
  }
}
